import { useCallback, useMemo, useState } from 'react';

import { Button, Input, Loader, Modal } from '@storybook';
import { GetInitials } from 'utils';
import './access-code-verification.scss';
import { useNetwork, useNotification } from 'hooks';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { IsValidAccessCode, SignDocumentState } from 'views/signing-dashboard';
import { API_URL } from 'constant';
import { EnvelopePurposeState } from 'states';
import { PrepareDashboardState } from 'views/prepare-dashboard';

interface IAccessCodeVerification {
	isModalOpen: boolean;
	envelopeId: string;
	recipientId: string;
	handleCloseModal: () => void;
}

function maskEmail(email: string) {
	if (!email) return '';
	const [user, domain] = email.split('@');
	if (!user || !domain) return email;
	const maskedUser =
		user.length <= 2
			? user[0] + '*'.repeat(user.length - 1)
			: user[0] + '*'.repeat(user.length - 2) + user[user.length - 1];
	return `${maskedUser}@${domain}`;
}

const AccessCodeVerification = ({
	isModalOpen,
	envelopeId,
	recipientId,
	handleCloseModal,
}: IAccessCodeVerification) => {
	const [accessCode, setAccessCode] = useState('');
	const [isNotifyLoading, setIsNotifyLoading] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');
	const { successNotification, errorNotification } = useNotification();
	const { post } = useNetwork();

	const singDocData = useRecoilValue(SignDocumentState);
	const envelopeData = useRecoilValue(PrepareDashboardState);
	const purpose = useRecoilValue(EnvelopePurposeState);
	const setIsValidAccessCode = useSetRecoilState(IsValidAccessCode);
	const { data: signData } = useMemo(
		() => (purpose === 'selfsign' ? { data: envelopeData } : singDocData),
		[singDocData, envelopeData, purpose]
	);

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setAccessCode(e.target.value);
			if (error) setError('');
		},
		[error]
	);

	const handleValidate = useCallback(async () => {
		try {
			setIsLoading(true);
			const resp = await post(API_URL.ACCESS_CODE, {
				accessCode,
				envelopeId,
				recipientId,
			});
			const status = resp?.response?.status;
			const apiData = resp?.apiData;
			const isSuccess = apiData?.data?.success;
			if (status === 200 && isSuccess) {
				setError('');
				const message =
					apiData?.data?.message ||
					apiData?.message ||
					'Access code validated successfully.';
				successNotification(message);
				setIsLoading(false);
				handleCloseModal();
				setIsValidAccessCode(true);
			} else {
				const message =
					apiData?.data?.message ||
					apiData?.message ||
					'Invalid access code. Please try again.';
				setError(message);
				errorNotification(message);
				setIsLoading(false);
			}
		} catch (err) {
			setError('Something went wrong. Please try again.');
			errorNotification('Something went wrong. Please try again.');
			setIsLoading(false);
		}
	}, [
		accessCode,
		envelopeId,
		errorNotification,
		handleCloseModal,
		post,
		recipientId,
		setIsValidAccessCode,
		successNotification,
	]);

	const handleNotifySender = useCallback(async () => {
		if (isNotifyLoading) return;
		try {
			setIsNotifyLoading(true);
			const resp = await post(API_URL.NOTIFY_SENDER, {
				envelopeId,
				recipientId,
			});
			const apiData = resp?.apiData;
			if (apiData?.success || apiData?.data?.success) {
				const message =
					apiData?.data?.message ||
					apiData?.message ||
					'Sender notified successfully.';
				successNotification(message);
			} else {
				const message =
					apiData?.data?.message ||
					apiData?.message ||
					'Failed to notify sender.';
				errorNotification(message);
			}
		} catch (err) {
			errorNotification('Something went wrong. Please try again.');
		} finally {
			setIsNotifyLoading(false);
		}
	}, [
		envelopeId,
		errorNotification,
		isNotifyLoading,
		post,
		recipientId,
		successNotification,
	]);

	const initials = useMemo(
		() => GetInitials(signData?.sender?.fullName || ''),
		[signData?.sender?.fullName]
	);

	return (
		<Modal
			className="AccessCodeVerification__modal"
			isOpen={isModalOpen}
			modalName="AccessCodeVerification__modal"
			closeModal={handleCloseModal}
			isStopOutsideClick={false}
			closeOnEscBtn={false}
			showCloseBtn={false}
		>
			<div className="AccessCodeVerification__wrapper">
				<div className="AccessCodeVerification__header">
					<p>Access Code Required to View This Document</p>
					<small className="AccessCodeVerification__sub-header">
						This document is secured. Please enter the access code provided to
						you to continue.
					</small>
				</div>
				<div className="AccessCodeVerification__signer">
					<span className="AccessCodeVerification__signer-initials">
						{initials}
					</span>
					<div className="AccessCodeVerification__signer-details">
						<p className="AccessCodeVerification__signer-name">
							{signData?.sender?.fullName}
						</p>
						<p className="AccessCodeVerification__signer-designation">
							{maskEmail(signData?.sender?.email || '')}
						</p>
					</div>
				</div>
				<div className="AccessCodeVerification__body">
					{error && (
						<div className="AccessCodeVerification__error-wrapper">
							<i className="ri-close-circle-fill"></i>
							<p className="AccessCodeVerification__error-message-wrapper">
								<p className="heading">{error}</p>
								<small className="error-message">
									Please reach out to the sender, or you can notify them by
									clicking the “Notify the sender” button below.
								</small>
							</p>
						</div>
					)}
					<div className="AccessCodeVerification__input-container">
						<Input
							inputType="text"
							label={'Access Code'}
							placeholder="Enter Access Code"
							autoFocus
							value={accessCode}
							handleChange={handleInputChange}
							isError={!!error}
						/>
						<Button
							label={
								isLoading ? (
									<Loader
										className="loader-blue"
										dimension={20}
										type="circle"
									/>
								) : (
									'Validate'
								)
							}
							className="AccessCodeVerification__validate-btn"
							handleClick={handleValidate}
							disabled={!accessCode.trim() || !!error}
						/>
					</div>
					<div className="AccessCodeVerification__resend-container">
						<p>
							Didn’t receive a access code?{' '}
							<span
								className="AccessCodeVerification__resend-btn"
								onClick={handleNotifySender}
							>
								{isNotifyLoading ? (
									<Loader
										className="loader-blue"
										dimension={20}
										type="circle"
									/>
								) : (
									'Notify the sender'
								)}
							</span>
						</p>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default AccessCodeVerification;
